// covered-by: frontends/carrie/src/routes/login/__tests__/LoginView.test.tsx
import { ButtonNew, Icon } from "@mg/dali/src";
import {
  type SsoAuthorizeResponse,
  type WorkosLoginResponse,
} from "@mg/schemas/src/prince/auth";
import { Text } from "@radix-ui/themes";
import { useMutation } from "@tanstack/react-query";
import { Navigate } from "@tanstack/react-router";
import { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { LoginForm } from "./LoginForm";
import { MfaForm } from "./MfaForm";
import { loginRoute } from "./route";

import { LoginLayout } from "../../components/layouts/login";
import {
  applyAskV2InPlaceReload,
  waitForAskV2Flag,
} from "../../config/askV2InPlace";
import {
  ssoAuthorize,
  ssoExchange,
  workOsLogin,
  verifyMfa,
} from "../../services/auth";
import { completeOnboarding } from "../../services/userProfile";
import { errorAnalyticsPayload, useAnalytics } from "../../utils/analytics";
import { isMeaningfulGigsRole } from "../../utils/auth";
import {
  defaultPostAuthLandingKind,
  ensureDefaultPostAuthLandingPath,
} from "../../utils/defaultPostAuthLanding";
import { useAppDispatch, useAppSelector } from "../../utils/hooks";
import { queryClient } from "../../utils/queryClient";
import { login } from "../../utils/slices/auth";
import { roiRoute } from "../roi/route";
import { ticketsRoute } from "../tickets/route";

function useWorkosLogin() {
  return useMutation({
    mutationKey: ["login"],
    mutationFn: workOsLogin,
  });
}

function useMfaVerification() {
  return useMutation({
    mutationKey: ["verifyMfa"],
    mutationFn: verifyMfa,
  });
}

export function LoginView() {
  const { t } = useTranslation("auth");
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.value);
  const searchParams = loginRoute.useSearch();
  const [authMode, setAuthMode] = useState<SsoAuthorizeResponse | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [mfaData, setMfaData] = useState<WorkosLoginResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [resolvedLandingPath, setResolvedLandingPath] = useState<string | null>(
    null,
  );
  const defaultLandingTelemetrySentRef = useRef(false);
  const ssoExchangeStartedRef = useRef(false);

  const loginMutation = useWorkosLogin();
  const mfaMutation = useMfaVerification();
  const authorizeMutation = useMutation({
    mutationKey: ["sso-authorize"],
    mutationFn: ssoAuthorize,
  });
  const onboardingMutation = useMutation({
    mutationKey: ["complete-onboarding-client"],
    mutationFn: completeOnboarding,
  });

  const posthog = useAnalytics("LoginView");

  const formRef = useRef<HTMLFormElement>(null);

  const validateForm = useCallback(() => {
    if (formRef.current) {
      const formData = new FormData(formRef.current);
      const email = formData.get("email") as string;
      const password = formData.get("password") as string;
      setIsFormValid(!!email && !!password);
    }
  }, []);

  const handleLoginSuccess = (data: WorkosLoginResponse, startTime: number) => {
    localStorage.setItem("onboarding_flow", "true");
    if (data.token) {
      onboardingMutation
        .mutateAsync()
        .then((dt) => {
          localStorage.setItem("token", dt.token);
          // Global from index.html / globals.d.ts
          sendTokenToServiceWorker();
          dispatch(login({ token: dt.token }));
        })
        .catch(() => {
          dispatch(login({ token: data.token as string }));
        });

      posthog.capture("login_success", {
        requestDurationSeconds: (performance.now() - startTime) / 1000,
        referredBy: user?.referredBy,
      });
    } else {
      posthog.capture("login_mfa_step", {
        requestDurationSeconds: (performance.now() - startTime) / 1000,
        referredBy: user?.referredBy,
      });
      setMfaData(data);
    }
  };

  // After WorkOS SSO callback redirect (#sso=1), exchange the short-lived cookie.
  useEffect(() => {
    if (user) return;
    if (typeof window === "undefined") return;
    if (!window.location.hash.includes("sso=1")) return;
    if (ssoExchangeStartedRef.current) return;
    ssoExchangeStartedRef.current = true;
    const startTime = performance.now();
    ssoExchange()
      .then((data) => {
        if (data?.token) {
          handleLoginSuccess(data, startTime);
          window.history.replaceState(
            null,
            "",
            window.location.pathname + window.location.search,
          );
        }
      })
      .catch((error: Error) => {
        setErrorMessage(error.message || "SSO login failed");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot on mount
  }, [user]);

  const startSsoRedirect = (email: string, enterpriseId?: string) => {
    authorizeMutation.mutate(
      { email, enterpriseId },
      {
        onSuccess: (data) => {
          if (data?.redirectUrl) {
            window.location.assign(data.redirectUrl);
            return;
          }
          if (data?.orgPicker?.length) {
            setAuthMode(data);
            setErrorMessage("Select an organization to continue with SSO.");
            return;
          }
          // password / mg / misconfigured SSO — never leave the button spinning
          // with a silent no-op (classic form always shows Continue with SSO).
          setErrorMessage(
            "SSO is not available for this email. Log in with your password, or contact your admin.",
          );
        },
        onError: (error) => setErrorMessage(error.message),
      },
    );
  };

  const handleLoginSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    const startTime = performance.now();
    setErrorMessage("");

    loginMutation.mutate(
      { email, password },
      {
        onSuccess: (data) => handleLoginSuccess(data!, startTime),
        onError: (error) => {
          setErrorMessage(error.message);
          posthog.capture("login_failure", {
            requestDurationSeconds: (performance.now() - startTime) / 1000,
            referredBy: user?.referredBy,
            ...errorAnalyticsPayload(error),
          });
        },
      },
    );
  };

  const handleMfaVerification = (otp: string) => {
    const startTime = performance.now();
    if (mfaData) {
      mfaMutation.mutateAsync(
        {
          authenticationChallengeId: mfaData.authenticationChallenge
            ?.id as string,
          pendingAuthenticationToken:
            mfaData.pendingAuthenticationToken as string,
          code: otp,
        },
        {
          onSuccess: (data) => {
            dispatch(login({ token: data!.token as string }));
            posthog.capture("mfa_success", {
              requestDurationSeconds: (performance.now() - startTime) / 1000,
              referredBy: user?.referredBy,
            });
          },
          onError: (error) => {
            setErrorMessage(error.message);
            posthog.capture("mfa_failure", {
              requestDurationSeconds: (performance.now() - startTime) / 1000,
              referredBy: user?.referredBy,
            });
          },
        },
      );
    }
  };

  const isAlreadyAuthed = user != null;
  const hasExplicitRedirect = searchParams.redirect != null;
  const needsAsyncDefault =
    isAlreadyAuthed && !hasExplicitRedirect && !isMeaningfulGigsRole();

  useEffect(() => {
    if (!needsAsyncDefault) return;
    if (resolvedLandingPath != null) return;
    let cancelled = false;
    void (async () => {
      const landingPromise = ensureDefaultPostAuthLandingPath(queryClient);
      const flagOn = await waitForAskV2Flag();
      if (cancelled) return;
      if (applyAskV2InPlaceReload(flagOn)) return;
      try {
        const target = await landingPromise;
        if (cancelled) return;
        setResolvedLandingPath(target);
      } catch {
        if (cancelled) return;
        setResolvedLandingPath(ticketsRoute.to);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [needsAsyncDefault, resolvedLandingPath]);

  useEffect(() => {
    if (!isAlreadyAuthed) return;
    if (hasExplicitRedirect) return;
    if (defaultLandingTelemetrySentRef.current) return;
    if (!isMeaningfulGigsRole() && resolvedLandingPath == null) return;
    posthog.capture("post_auth_default_landing", {
      site: "login_already_authed",
      target: isMeaningfulGigsRole() ? "roi" : defaultPostAuthLandingKind(),
    });
    defaultLandingTelemetrySentRef.current = true;
  }, [isAlreadyAuthed, hasExplicitRedirect, resolvedLandingPath]);

  if (user) {
    let searchRedirect: string | null;

    if (searchParams.redirect != null) {
      const { redirect } = searchParams;
      if (redirect.startsWith("/")) {
        searchRedirect = redirect;
      } else {
        searchRedirect = `/${redirect}`;
      }
    } else if (isMeaningfulGigsRole()) {
      searchRedirect = roiRoute.to;
    } else {
      searchRedirect = resolvedLandingPath;
    }

    if (searchRedirect == null) {
      return (
        <LoginLayout>
          <div
            data-testid="login-resolving-landing"
            aria-busy="true"
            aria-label={t("login.resolvingLandingAriaLabel")}
          />
        </LoginLayout>
      );
    }

    return <Navigate to={searchRedirect} />;
  }

  return (
    <LoginLayout>
      {!mfaData ? (
        <>
          {searchParams.error ? (
            <Text color="red" size="2" className="mb-4 block">
              {searchParams.error === "session-expired"
                ? t("login.sessionExpiredError")
                : null}
            </Text>
          ) : null}
          {authMode?.orgPicker?.length ? (
            <div
              className="mb-4 grid gap-2"
              data-testid="login-org-picker"
              role="list"
              aria-label="Choose organization"
            >
              {authMode.orgPicker.map((org) => (
                <ButtonNew
                  key={org.enterpriseId}
                  variant="outlined"
                  data-testid={`login-org-option-${org.enterpriseId}`}
                  aria-label={`Continue with ${org.name}`}
                  onClick={() => {
                    const email = formRef.current
                      ? (new FormData(formRef.current).get("email") as string)
                      : "";
                    startSsoRedirect(email, org.enterpriseId);
                  }}
                >
                  {org.name}
                </ButtonNew>
              ))}
            </div>
          ) : null}
          <LoginForm
            data-testid="login-form"
            formRef={formRef}
            isValid={isFormValid}
            onSubmit={handleLoginSubmit}
            loading={loginMutation.isPending}
            disabled={loginMutation.isPending}
            error={loginMutation.isError || Boolean(errorMessage)}
            errorMessage={errorMessage}
            checkValidity={validateForm}
            ssoLoading={authorizeMutation.isPending}
            onSsoClick={() => {
              const email = formRef.current
                ? (new FormData(formRef.current).get("email") as string)
                : "";
              if (!email) {
                setErrorMessage("Enter your work email to continue with SSO.");
                return;
              }
              setErrorMessage("");
              startSsoRedirect(email);
            }}
          />
        </>
      ) : (
        <div>
          <ButtonNew
            variant="ghost"
            data-testid="login-mfa-back-button"
            aria-label={t("login.mfaBackButtonAriaLabel")}
            onClick={() => {
              setMfaData(null);
            }}
          >
            <Icon.ArrowLeft />
            {t("login.backButtonLabel")}
          </ButtonNew>
          <MfaForm
            mfaOptions={mfaData}
            loading={mfaMutation.isPending}
            error={mfaMutation.isError}
            onVerify={handleMfaVerification}
            errorMessage={errorMessage}
          />
        </div>
      )}
    </LoginLayout>
  );
}
